/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  onConnect,
  reatomAsync,
  sleep,
  withAbort,
  withAssign,
} from '@reatom/framework'

import { selectedNetworkAtom } from '@root/entities/network'
import { QUICK_BUY_AMOUNT_NATIVE } from '@root/entities/trade-settings/model/trade-settings'
import { NumericFormat } from '@root/shared/core/format'
import { reatomNumericInput } from '@root/shared/lib'

import {
  quickSwapAmountBySectionAtom,
  updateQuickBuyAmountAction,
} from '../../trade-settings/atoms'

import type { TradeSettingsQuick } from '@root/entities/trade-settings/model'

export const reatomQuickBuyInput = ({
  section,
}: {
  section: TradeSettingsQuick.QuickSwapSection
}) => {
  const sectionAmountAtom = atom((ctx) => {
    return ctx.spy(quickSwapAmountBySectionAtom)?.[section].buy
  })

  const amountAtom = reatomNumericInput({
    initialAmount: (ctx) => {
      const amountNative = ctx.get(quickSwapAmountBySectionAtom)?.[section].buy
      return amountNative
    },
    optional: true,
  })

  const isValidAtom = atom((ctx) => {
    const network = ctx.spy(selectedNetworkAtom)
    const amount = ctx.spy(amountAtom)
    if (!amount) {
      return true
    }
    return amount.lte(QUICK_BUY_AMOUNT_NATIVE[network].max)
  })

  const updateAction = reatomAsync(async (ctx) => {
    const amount = ctx.get(amountAtom)
    const isValid = ctx.get(isValidAtom)
    const currentAmount = ctx.get(quickSwapAmountBySectionAtom)?.[section].buy
    if (!amount || !isValid || !currentAmount || currentAmount.eq(amount)) {
      return
    }
    await ctx.schedule(() => sleep(500))
    updateQuickBuyAmountAction(ctx, { amount, section })
  }).pipe(withAbort())

  const resetAction = action((ctx) => {
    const quickBuyAmount = ctx.get(quickSwapAmountBySectionAtom)?.[section].buy
    if (!quickBuyAmount) {
      return
    }
    amountAtom.setRaw(ctx, NumericFormat.formatPreset(quickBuyAmount, 'full'))
  })

  onConnect(amountAtom, (ctx) => {
    const unsub = amountAtom.onChange((ctx) => {
      updateAction(ctx)
    })
    const unsubCurrent = ctx.subscribe(sectionAmountAtom, (amount) => {
      if (!amount) {
        return
      }
      amountAtom.setRaw(ctx, NumericFormat.formatPreset(amount, 'full'))
    })

    return () => {
      unsub()
      unsubCurrent()
    }
  })

  return amountAtom.pipe(
    withAssign(() => ({
      isValid: isValidAtom,
      reset: resetAction,
      update: updateAction,
    })),
  )
}
